import React, { useState } from "react";
import { useUser, SignUpButton } from "@clerk/clerk-react";
import { ArrowRight, ChevronDown } from "lucide-react";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Display,
  Hairline,
  SEO,
  Card,
} from "../ui";
import { canonicalFor } from "../../routes";

export interface UseCaseConfig {
  /** URL path for canonical + breadcrumbs. */
  path: string;
  /** SEO title. */
  seoTitle: string;
  /** SEO description. */
  seoDescription: string;
  /** Eyebrow above hero, e.g. "For recruiters". */
  eyebrow: string;
  /** Hero headline. Sentence-case + terminal period. */
  headline: string;
  /** One word from `headline` to italicize. */
  italicWord?: string;
  /** Subhead paragraph. */
  subhead: string;
  /** Persona-specific link types — used in the hero card mockup. */
  linkExamples: { label: string; sub?: string }[];
  /** What you get — three editorial blocks. */
  features: { title: string; body: string }[];
  /** How they use it — three numbered steps. */
  steps: { title: string; body: string }[];
  /** FAQ — 3–5 entries, long-form answers. */
  faq: { q: string; a: string }[];
  /** Mock card preview content — name + title shown in the hero card. */
  preview: { name: string; title: string; handle: string };
}

export function UseCase(config: UseCaseConfig) {
  const { isSignedIn, user } = useUser();
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  // Personalize the hero card mockup for signed-in visitors. We override only
  // the *name* — the persona-specific title and handle stay so the demo card
  // still reads as a recruiter/sales/etc. example, just with the visitor's name
  // on it. Falls back to the configured preview.name (Sean Thomas) for guests.
  const previewName = (isSignedIn && user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.fullName || ''
    : '') || config.preview.name;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: config.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "WhoBrew", item: "https://whobrew.us/" },
      { "@type": "ListItem", position: 2, name: "For",     item: "https://whobrew.us/" },
      { "@type": "ListItem", position: 3, name: config.eyebrow.replace(/^For\s+/i, ""), item: canonicalFor(config.path) },
    ],
  };

  return (
    <>
      <SEO
        title={config.seoTitle}
        description={config.seoDescription}
        canonical={canonicalFor(config.path)}
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />

      {/* HERO */}
      <Section tone="page" spacing="loose" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <Eyebrow>{config.eyebrow}</Eyebrow>
              <Display
                as="h1"
                scale="lg"
                italicWord={config.italicWord}
                className="mt-5"
              >
                {config.headline}
              </Display>
              <p className="mt-7 text-lg text-coffee-700 dark:text-cream-200 max-w-xl leading-relaxed">
                {config.subhead}
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                {isSignedIn ? (
                  <Button to="/my-card" size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>
                    Open my card
                  </Button>
                ) : (
                  <SignUpButton mode="modal">
                    <Button size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>
                      Create your card
                    </Button>
                  </SignUpButton>
                )}
                <Button to="/how-it-works" variant="secondary" size="lg">
                  How it works
                </Button>
              </div>
            </div>

            {/* Persona card mockup */}
            <div className="lg:col-span-5">
              <Card tone="flat" padded={false} className="overflow-hidden border border-latte-300 dark:border-latte-800 max-w-sm mx-auto">
                <div className="bg-coffee-900 dark:bg-cream-100 h-20" />
                <div className="text-center px-5 pb-4 -mt-10">
                  <div className="w-16 h-16 mx-auto rounded-full bg-coffee-700 flex items-center justify-center text-cream-50 font-display text-xl border-4 border-cream-50 dark:border-coffee-900">
                    {previewName.split(" ").map((p: string) => p[0]).join("").slice(0, 2)}
                  </div>
                  <p className="mt-3 font-display text-title text-coffee-900 dark:text-cream-50">
                    {previewName}
                  </p>
                  <p className="text-body-sm text-coffee-700 dark:text-cream-200">{config.preview.title}</p>
                </div>
                <div className="px-5 pb-5 space-y-2">
                  {config.linkExamples.slice(0, 4).map((l) => (
                    <div
                      key={l.label}
                      className="flex items-center gap-3 px-3 py-2.5 bg-cream-100 dark:bg-coffee-800 border border-latte-300 dark:border-latte-800 rounded"
                    >
                      <span className="font-mono text-caption text-coffee-600 dark:text-cream-200 w-6">·</span>
                      <span className="text-body-sm text-coffee-900 dark:text-cream-50 flex-1">{l.label}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </Container>
      </Section>

      {/* LINK TYPES BELT */}
      <Section tone="raised" spacing="tight" hairlineBottom>
        <Container>
          <div className="text-center">
            <Eyebrow>Link types {config.eyebrow.toLowerCase().replace(/^for\s+/i, "")} actually use</Eyebrow>
            <div className="mt-6 flex flex-wrap justify-center gap-x-3 gap-y-3 max-w-4xl mx-auto">
              {config.linkExamples.map((l) => (
                <span
                  key={l.label}
                  className="font-mono text-caption text-coffee-700 dark:text-cream-200 border border-latte-300 dark:border-latte-700 px-3 py-2 rounded"
                >
                  {l.label}
                </span>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* FEATURES — three editorial blocks */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="max-w-3xl mb-14">
            <Eyebrow>What you get</Eyebrow>
            <Display as="h2" scale="md" italicWord="actually" className="mt-4">
              Everything {config.eyebrow.toLowerCase().replace(/^for\s+/i, "")} actually use.
            </Display>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-latte-300 dark:bg-latte-800 border border-latte-300 dark:border-latte-800 rounded-lg overflow-hidden">
            {config.features.map((f, i) => (
              <div key={i} className="bg-cream-50 dark:bg-coffee-900 p-8">
                <Eyebrow step={i + 1} of={config.features.length}>Feature</Eyebrow>
                <Display as="h3" scale="sm" className="mt-3 text-2xl">
                  {f.title}
                </Display>
                <p className="mt-4 text-body-sm text-coffee-700 dark:text-cream-200 leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* STEPS */}
      <Section tone="raised" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>How it works in your day</Eyebrow>
          <Display as="h2" scale="md" italicWord="day" className="mt-4 mb-12">
            From sign-up to a real conversation.
          </Display>
          <div className="space-y-12">
            {config.steps.map((s, i) => (
              <div key={i}>
                <Eyebrow step={i + 1} of={config.steps.length}>Step</Eyebrow>
                <Display as="h3" scale="sm" className="mt-3">
                  {s.title}
                </Display>
                <p className="mt-4 text-body text-coffee-700 dark:text-cream-200 leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Common questions</Eyebrow>
          <Display as="h2" scale="md" italicWord="ask" className="mt-4 mb-12">
            What you'll likely ask.
          </Display>
          <div className="divide-y divide-latte-300 dark:divide-latte-800 border-y border-latte-300 dark:border-latte-800">
            {config.faq.map((item, i) => {
              const open = openFAQ === i;
              return (
                <div key={i}>
                  <button
                    onClick={() => setOpenFAQ(open ? null : i)}
                    aria-expanded={open}
                    className="w-full text-left py-6 flex items-start justify-between gap-6"
                  >
                    <span className="font-display text-headline text-coffee-900 dark:text-cream-50 leading-snug">
                      {item.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 mt-2 text-coffee-700 dark:text-cream-200 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  {open && (
                    <div className="pb-8 text-body text-coffee-700 dark:text-cream-200 leading-relaxed">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Container>
      </Section>

      <Hairline />

      {/* CTA */}
      <Section tone="ink" spacing="default">
        <Container className="text-center">
          <Display as="h2" scale="lg" italicWord="card" className="mx-auto max-w-3xl">
            Make a card you actually want to hand out.
          </Display>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            {isSignedIn ? (
              <Button to="/my-card" size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
                Open my card
              </Button>
            ) : (
              <SignUpButton mode="modal">
                <Button size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
                  Create your card
                </Button>
              </SignUpButton>
            )}
            <Button to="/order-card" variant="secondary" size="lg" onInk>
              Order an NFC card
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
}

export default UseCase;
